import React, { useState, useEffect } from "react";
import {
  STRIPE_PRODUCTS,
  StripeProduct,
} from "../../lib/config/stripe-products";
import { useAuth } from "../../hooks/useSupabaseAuth"

interface StripeCheckoutProps {
  selectedTimeSlot?: string;
  preselectedSessionType?: string | null;
  onSuccess?: (sessionId: string) => void;
  onError?: (error: string) => void;
  clientData?: any;
}

const StripeCheckout: React.FC<StripeCheckoutProps> = ({
  selectedTimeSlot,
  preselectedSessionType,
  // onSuccess, // TODO: Add success handler if needed
  onError,
  clientData,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<StripeProduct | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [showProductSelection, setShowProductSelection] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const { isAuthenticated, isLoading: authLoading, error: authErrorState } = useAuth();

  // Check authentication and set auth error if needed
  useEffect(() => {
    if (authErrorState) {
      setAuthError(authErrorState?.message || 'Authentication error');
    } else if (!authLoading && !isAuthenticated) {
      setAuthError("You must be signed in to make a payment. Please sign in or create an account first.");
    } else {
      setAuthError(null);
    }
  }, [isAuthenticated, authLoading, authErrorState]);

  // Auto-select product based on preselected session type
  React.useEffect(() => {
    if (preselectedSessionType) {
      let productToSelect: StripeProduct | null = null;
      
      // Map session types to products
      switch (preselectedSessionType.toLowerCase()) {
        case 'one-on-one':
        case 'consultation':
        case 'initial':
          productToSelect = STRIPE_PRODUCTS.find(p => p.id === 'consultation-session-60min') || null;
          break;
        case 'follow-up':
          productToSelect = STRIPE_PRODUCTS.find(p => p.id === 'follow-up-session-30min') || null;
          break;
        case 'package':
        case 'wellness-package':
          productToSelect = STRIPE_PRODUCTS.find(p => p.id === 'wellness-package-3sessions') || null;
          break;
        default:
          // Default to one-on-one consultation for intake form
          productToSelect = STRIPE_PRODUCTS.find(p => p.id === 'consultation-session-60min') || null;
      }
      
      if (productToSelect) {
        setSelectedProduct(productToSelect);
        setShowProductSelection(false);
      }
    }
  }, [preselectedSessionType]);

  const formatPrice = (priceInCents: number): string => {
    return `$${(priceInCents / 100).toFixed(2)}`;
  };

  const formatTimeSlot = (timeSlot: string): string => {
    return new Date(timeSlot).toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };

  const handleProductSelect = (product: StripeProduct) => {
    setSelectedProduct(product);
    setShowProductSelection(false);
  };

  const handleCheckout = async () => {
    if (!selectedProduct) {
      onError?.("Please select a session type");
      return;
    }

    // Check authentication before proceeding
    if (!isAuthenticated) {
      const errorMessage = "You must be signed in to make a payment. Please sign in or create an account first.";
      setAuthError(errorMessage);
      onError?.(errorMessage);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies for auth
        body: JSON.stringify({
          productId: selectedProduct.id,
          timeSlot: selectedTimeSlot,
          successUrl: `${window.location.origin}/booking-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/booking-cancelled`,
          customerEmail: clientData?.email,
          customerName: clientData?.first_name && clientData?.last_name 
            ? `${clientData.first_name} ${clientData.last_name}` 
            : undefined,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to create checkout session");
      }

      const data = await response.json();

      // Redirect to Stripe Checkout
      if (data.success && data.data.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error("Checkout error:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to start checkout process";
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green-600 mx-auto mb-4"></div>
          <p className="text-brand-stone-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Show authentication error if not authenticated
  if (authError && !isAuthenticated) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-brand-green-800 mb-2">Authentication Required</h3>
          <p className="text-brand-stone-600 mb-4">{authError}</p>
          <div className="text-sm text-brand-stone-500">
            <p>Please sign in to continue with your booking.</p>
          </div>
        </div>
      </div>
    );
  }

  if (showProductSelection) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-brand-green-800 mb-4">
          Select Session Type
        </h3>

        <div className="space-y-4">
          {STRIPE_PRODUCTS.map((product) => (
            <div
              key={product.id}
              className="border border-brand-stone-200 rounded-lg p-4 hover:border-brand-green-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => handleProductSelect(product)}
            >
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-brand-green-700">
                  {product.name}
                </h4>
                <span className="text-xl font-bold text-brand-green-800">
                  {formatPrice(product.priceInCents)}
                </span>
              </div>

              <p className="text-brand-stone-600 text-sm mb-3">
                {product.description}
              </p>

              <div className="flex flex-wrap gap-2">
                {product.metadata?.duration && (
                  <span className="px-2 py-1 bg-brand-green-100 text-brand-green-700 text-xs rounded-full">
                    {product.metadata.duration} minutes
                  </span>
                )}
                {product.metadata?.type && (
                  <span className="px-2 py-1 bg-brand-stone-100 text-brand-stone-700 text-xs rounded-full">
                    {product.metadata.type}
                  </span>
                )}
                {product.metadata?.savings && (
                  <span className="px-2 py-1 bg-brand-green-200 text-brand-green-800 text-xs rounded-full">
                    Save {formatPrice(parseInt(product.metadata.savings))}
                  </span>
                )}
              </div>

              <button className="w-full mt-3 px-4 py-2 bg-brand-green-600 text-white font-medium rounded-lg hover:bg-brand-green-700 transition-colors">
                Select This Session
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h3 className="text-lg font-semibold text-brand-green-800 mb-4">
        Booking Summary
      </h3>

      {selectedProduct && (
        <div className="mb-6">
          <div className="border border-brand-stone-200 rounded-lg p-4 bg-brand-stone-50">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-semibold text-brand-green-700">
                {selectedProduct.name}
              </h4>
              <span className="text-xl font-bold text-brand-green-800">
                {formatPrice(selectedProduct.priceInCents)}
              </span>
            </div>

            <p className="text-brand-stone-600 text-sm mb-3">
              {selectedProduct.description}
            </p>

            {selectedTimeSlot && (
              <div className="mt-3 p-3 bg-white rounded border border-brand-green-200">
                <p className="text-sm font-medium text-brand-green-800">
                  Scheduled for:
                </p>
                <p className="text-brand-green-700">
                  {formatTimeSlot(selectedTimeSlot)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => setShowProductSelection(true)}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-brand-stone-300 text-brand-stone-700 rounded-lg hover:bg-brand-stone-50 transition-colors disabled:opacity-50"
        >
          Change Session
        </button>

        <button
          onClick={handleCheckout}
          disabled={loading || !selectedProduct}
          className="flex-1 px-4 py-2 bg-brand-green-600 text-white font-medium rounded-lg hover:bg-brand-green-700 transition-colors disabled:opacity-50 flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing...
            </>
          ) : (
            "Proceed to Payment"
          )}
        </button>
      </div>

      <div className="mt-4 text-xs text-brand-stone-500 space-y-1">
        <p>• Secure payment processing by Stripe</p>
        <p>• You will receive a confirmation email after payment</p>
        <p>• A calendar invite will be sent for scheduled sessions</p>
        {!selectedTimeSlot && (
          <p>• Hailey will contact you to schedule your session time</p>
        )}
      </div>
    </div>
  );
};

export default StripeCheckout;
